/* eslint-disable no-unused-vars, jsx-a11y/alt-text, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiWifiOff, FiWifi, FiRefreshCw, FiClock } from 'react-icons/fi';
import { useLanguage } from '../../context/LanguageContext';

/**
 * OfflineIndicator — v2
 * يعرض شريط Offline ذكي مع:
 * - عدد الطلبات المعلقة
 * - حالة المزامنة
 * - رسالة عودة الاتصال
 */
const OfflineIndicator = ({ pendingCount = 0, isSyncing = false }) => {
    const { t } = useLanguage();
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [showReconnected, setShowReconnected] = useState(false);

    useEffect(() => {
        const handleOnline = () => {
            setIsOffline(false);
            // عرض رسالة "عاد الاتصال" لمدة 3 ثواني
            setShowReconnected(true);
            setTimeout(() => setShowReconnected(false), 3000);
        };
        const handleOffline = () => {
            setIsOffline(true);
            setShowReconnected(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return (
        <AnimatePresence>
            {/* شريط انقطاع الاتصال */}
            {isOffline && (
                <motion.div
                    key="offline"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-rose-600 text-white sticky top-0 z-50 print:hidden"
                >
                    <div className="flex items-center justify-between px-4 py-2 max-w-screen-xl mx-auto">
                        <div className="flex items-center gap-2">
                            <FiWifiOff className="animate-pulse flex-shrink-0" size={16} />
                            <span className="font-bold text-sm">
                                وضع عدم الاتصال — النظام يعمل محلياً
                            </span>
                        </div>
                        {pendingCount > 0 && (
                            <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                                <FiClock size={14} />
                                <span className="text-xs font-black">
                                    {pendingCount} طلب معلق
                                </span>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* شريط المزامنة */}
            {!isOffline && isSyncing && (
                <motion.div
                    key="syncing"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-amber-500 text-white sticky top-0 z-50 print:hidden"
                >
                    <div className="flex items-center justify-center gap-2 px-4 py-2">
                        <FiRefreshCw className="animate-spin" size={14} />
                        <span className="font-bold text-sm">
                            جاري مزامنة {pendingCount} طلب معلق...
                        </span>
                    </div>
                </motion.div>
            )}

            {/* شريط عودة الاتصال */}
            {showReconnected && !isSyncing && (
                <motion.div
                    key="reconnected"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-emerald-500 text-white sticky top-0 z-50 print:hidden"
                >
                    <div className="flex items-center justify-center gap-2 px-4 py-2">
                        <FiWifi size={14} />
                        <span className="font-bold text-sm">
                            ✅ عاد الاتصال — يتم مزامنة الطلبات المعلقة
                        </span>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default OfflineIndicator;
